import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Filter, Search, X, Pencil } from 'lucide-react';
import EnhancedEOIResponsesTable from './EnhancedEOIResponsesTable';
import { TableColumn, ColumnWidth } from '../../../components/common/EnhancedTable/types';
import './EnhancedEOIResponses.css';
import { supabase } from '../../../lib/supabase';
import { formatTextWithLinks } from '../../../utils/formatters';
import { usePermissions } from '../../../hooks/usePermissions';
import AddEOIResponseModal from '../../../pages/Admin/EOIResponses/components/AddEOIResponseModal';
import EditEOIResponseModal from '../../../pages/Admin/EOIResponses/components/EditEOIResponseModal';
import type { EOIResponse, EOIAttachment } from '../../../types/eoi-response';
import type { LeaseEOI } from '../../../types/lease-eoi';
import type { Applicant } from '../../../types/applicant';

// Extended EOI Response type with related details
interface EnhancedEOIResponse extends EOIResponse {
  eoiDetails?: Pick<LeaseEOI, 'eoi_id' | 'eoi_lot'>;
  applicantDetails?: Pick<Applicant, 'appl_id' | 'appl_name'>;
}
import { formatDate } from '../../../utils/dateUtils';

// Define the sort field type
type SortField = 'reoi_id' | 'reoi_eoi' | 'reoi_app' | 'reoi_date' | 'reoi_success';

// Define the sort config type
type SortConfig = {
  field: SortField;
  direction: 'asc' | 'desc';
};

// Define the filter type
type EOIResponseFilter = {
  eoiReference: string | null;
  applicant: string | null;
  dateAfter: string | null;
  dateBefore: string | null;
  status: string | null;
};

export default function EnhancedEOIResponses() {
  const { canManageSystemSettings, isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  
  // State for filters, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<EOIResponseFilter>({
    eoiReference: null,
    applicant: null,
    dateAfter: null,
    dateBefore: null,
    status: null
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'reoi_date',
    direction: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // State for modals
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<EOIResponse | undefined>();

  // Fetch EOI responses data
  const { data: responses = [], isLoading, error: fetchError } = useQuery({
    queryKey: ['eoi-responses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eoi_responses')
        .select(`
          *,
          eoi_response_attachments (*)
        `)
        .order('reoi_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Fetch related EOIs and applicants
      const eoiIds = [...new Set(data.map(r => r.reoi_eoi).filter(id => id && !isNaN(parseInt(id))))];
      const appIds = [...new Set(data.map(r => r.reoi_app).filter(id => id && !isNaN(parseInt(id))))];
      
      const [eoisResult, applicantsResult] = await Promise.all([
        eoiIds.length > 0 ? supabase.from('lease_eois').select('eoi_id, eoi_lot').in('eoi_id', eoiIds) : { data: [] },
        appIds.length > 0 ? supabase.from('applicants').select('appl_id, appl_name').in('appl_id', appIds) : { data: [] }
      ]);
      
      // Create lookup maps
      const eoiMap = new Map(eoisResult.data?.map(e => [e.eoi_id.toString(), e]));
      const appMap = new Map(applicantsResult.data?.map(a => [a.appl_id.toString(), a]));
      
      // Enhance response data with related info
      return data.map(response => ({
        ...response,
        eoiDetails: eoiMap.get(response.reoi_eoi),
        applicantDetails: appMap.get(response.reoi_app)
      }));
    }
  });

  // Extract unique values for filter dropdowns
  const uniqueEOIs = useMemo(() => {
    const eois = responses
      .filter(r => r.reoi_eoi && r.eoiDetails)
      .map(r => ({ id: r.reoi_eoi, label: `${r.reoi_eoi} - ${r.eoiDetails?.eoi_lot || ''}` }));
    
    // Remove duplicates
    return Array.from(new Map(eois.map(item => [item.id, item])).values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [responses]);

  const uniqueApplicants = useMemo(() => {
    const applicants = responses
      .filter(r => r.reoi_app && r.applicantDetails)
      .map(r => ({ id: r.reoi_app, label: r.applicantDetails?.appl_name || r.reoi_app }));
    
    // Remove duplicates
    return Array.from(new Map(applicants.map(item => [item.id, item])).values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [responses]);

  // Apply filters and search
  const filteredResponses = useMemo(() => {
    if (!responses) return [];

    return responses.filter(response => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = 
          (response.reoi_eoi || '').toLowerCase().includes(searchLower) ||
          (response.reoi_app || '').toLowerCase().includes(searchLower) ||
          (response.eoiDetails?.eoi_lot || '').toLowerCase().includes(searchLower) ||
          (response.applicantDetails?.appl_name || '').toLowerCase().includes(searchLower) ||
          (response.reoi_application || '').toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // EOI Reference filter
      if (filters.eoiReference && response.reoi_eoi !== filters.eoiReference) {
        return false;
      }

      // Applicant filter
      if (filters.applicant && response.reoi_app !== filters.applicant) {
        return false;
      }

      // Date after filter
      if (filters.dateAfter && response.reoi_date < filters.dateAfter) {
        return false;
      }

      // Date before filter
      if (filters.dateBefore && response.reoi_date > filters.dateBefore) {
        return false;
      }

      // Status filter
      if (filters.status && response.reoi_success !== filters.status) {
        return false;
      }

      return true;
    });
  }, [responses, searchQuery, filters]);

  // Apply sorting
  const sortedResponses = useMemo(() => {
    return [...filteredResponses].sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'reoi_id': 
          return direction * (a.reoi_id - b.reoi_id);
        case 'reoi_eoi': 
          return direction * (a.reoi_eoi || '').localeCompare(b.reoi_eoi || '');
        case 'reoi_app': 
          const aName = a.applicantDetails?.appl_name || a.reoi_app || '';
          const bName = b.applicantDetails?.appl_name || b.reoi_app || '';
          return direction * aName.localeCompare(bName);
        case 'reoi_date': 
          return direction * (a.reoi_date || '').localeCompare(b.reoi_date || '');
        case 'reoi_success': 
          return direction * (a.reoi_success || '').localeCompare(b.reoi_success || '');
        default: 
          return 0;
      }
    });
  }, [filteredResponses, sortConfig]);

  // Apply pagination
  const paginatedResponses = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedResponses.slice(startIndex, endIndex);
  }, [sortedResponses, currentPage, pageSize]);

  // Handle sorting
  const handleSort = (sortConfig: { field: string; direction: 'asc' | 'desc' }) => {
    console.log('Sorting:', sortConfig);
    setSortConfig(sortConfig as SortConfig);
  };

  // Handle sort button clicks
  const handleSortButtonClick = (field: SortField) => {
    setSortConfig(current => ({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: Partial<EOIResponseFilter>) => {
    setFilters(current => ({ ...current, ...newFilters }));
  };

  // Handle removing a filter
  const handleRemoveFilter = (key: keyof EOIResponseFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Helper function to get status badge
  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'Y':
        return <div className="status-badge successful">Successful</div>;
      case 'N':
        return <div className="status-badge unsuccessful">Unsuccessful</div>;
      case 'P':
        return <div className="status-badge under-review">Under Review</div>;
      default:
        return <div className="status-badge unknown">{status || 'Unknown'}</div>;
    }
  };

  // Calculate pagination configuration
  const paginationConfig = {
    currentPage,
    pageSize,
    totalCount: sortedResponses.length,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [25, 50, 100, 200]
  };

  // Define table columns
  const columns: TableColumn<EnhancedEOIResponse>[] = [
    {
      id: 'actions',
      header: 'Edit',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            onClick={() => {
              setSelectedResponse(row);
              setIsFormModalOpen(true);
            }}
            className="action-button edit"
            title="Edit EOI Response"
          >
            <Pencil className="h-5 w-5" />
          </button>
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      visibleWhen: (permissions) => permissions.isAdmin || permissions.canManageSystemSettings
    },
    {
      id: 'reoi_id',
      header: 'Response ID',
      accessor: (row) => (
        <div className="truncate">
          {row.reoi_id}
        </div>
      ),
      width: 'narrow' as ColumnWidth,
      sortable: true
    },
    {
      id: 'reoi_eoi',
      header: 'EOI Reference',
      accessor: (row) => (
        <div className="truncate" title={row.eoiDetails ? `${row.reoi_eoi} - ${row.eoiDetails.eoi_lot}` : row.reoi_eoi || 'Not specified'}>
          {row.eoiDetails ? `${row.reoi_eoi} - ${row.eoiDetails.eoi_lot}` : row.reoi_eoi || 'Not specified'}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'reoi_app',
      header: 'EOI Applicant',
      accessor: (row) => (
        <div className="truncate" title={row.applicantDetails ? row.applicantDetails.appl_name : row.reoi_app || 'Not specified'}>
          {row.applicantDetails ? row.applicantDetails.appl_name : row.reoi_app || 'Not specified'}
        </div>
      ),
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'reoi_date',
      header: 'Submission Date',
      accessor: (row) => {
        const formattedDate = formatDate(row.reoi_date);
        return (
          <div className="truncate" title={formattedDate}>
            {formattedDate}
          </div>
        );
      },
      width: 'medium' as ColumnWidth,
      sortable: true
    },
    {
      id: 'reoi_success',
      header: 'Status',
      accessor: (row) => getStatusBadge(row.reoi_success),
      width: 'narrow' as ColumnWidth,
      sortable: true
    },
    {
      id: 'reoi_application',
      header: 'Notes and Docs',
      accessor: (row) => (
        <div className="truncate" title={row.reoi_application || 'Not specified'}>
          {formatTextWithLinks(row.reoi_application || 'Not specified')}
        </div>
      ),
      width: 'wide' as ColumnWidth
    }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-4 text-red-500">
        Error loading EOI response data. Please try again later.
      </div>
    );
  }

  return (
    <div className="enhanced-eoi-responses-container p-4">
      <div className="enhanced-eoi-responses-header">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">EOI Responses</h1>
            
            {/* Add button - only visible to users with admin privileges */}
            {(isAdmin || canManageSystemSettings) && (
              <button 
                onClick={() => {
                  setSelectedResponse(undefined);
                  setIsFormModalOpen(true);
                }}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Response
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Search input */}
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                placeholder="Search EOI responses..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            {/* Filter button */}
            <button
              className="p-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Toggle filters"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
              {Object.values(filters).filter(Boolean).length > 0 && (
                <span className="ml-1 bg-primary text-white text-xs rounded-full px-2 py-0.5">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
            
            <div className="text-base font-bold text-gray-700">
              Total Records: {filteredResponses.length}
            </div>
          </div>

          {/* Active filters */}
          {Object.values(filters).some(Boolean) && (
            <div className="active-filters">
              {filters.eoiReference && (
                <div className="filter-tag">
                  <span className="filter-tag-label">EOI:</span>
                  <span>{uniqueEOIs.find(e => e.id === filters.eoiReference)?.label || filters.eoiReference}</span>
                  <button
                    onClick={() => handleRemoveFilter('eoiReference')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.applicant && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Applicant:</span>
                  <span>{uniqueApplicants.find(a => a.id === filters.applicant)?.label || filters.applicant}</span>
                  <button
                    onClick={() => handleRemoveFilter('applicant')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.dateAfter && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Date After:</span>
                  <span>{formatDate(filters.dateAfter)}</span>
                  <button
                    onClick={() => handleRemoveFilter('dateAfter')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.dateBefore && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Date Before:</span>
                  <span>{formatDate(filters.dateBefore)}</span>
                  <button
                    onClick={() => handleRemoveFilter('dateBefore')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              {filters.status && (
                <div className="filter-tag">
                  <span className="filter-tag-label">Status:</span>
                  <span>
                    {filters.status === 'Y' ? 'Successful' : 
                     filters.status === 'N' ? 'Unsuccessful' : 
                     filters.status === 'P' ? 'Under Review' : filters.status}
                  </span>
                  <button
                    onClick={() => handleRemoveFilter('status')}
                    className="filter-tag-remove"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Filter panel */}
          {isFilterOpen && (
            <div className="filter-panel">
              <div className="filter-panel-header">
                <h3 className="font-medium text-gray-700">Filters</h3>
                <button
                  onClick={() => setFilters({
                    eoiReference: null,
                    applicant: null,
                    dateAfter: null,
                    dateBefore: null,
                    status: null
                  })}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="filter-panel-body">
                {/* EOI Reference filter */}
                <div className="filter-group">
                  <label htmlFor="eoi-reference-filter">EOI Reference</label>
                  <select
                    id="eoi-reference-filter"
                    value={filters.eoiReference || ''}
                    onChange={(e) => handleFilterChange({ eoiReference: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All EOIs</option>
                    {uniqueEOIs.map(eoi => (
                      <option key={eoi.id} value={eoi.id}>{eoi.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Applicant filter */}
                <div className="filter-group">
                  <label htmlFor="applicant-filter">Applicant</label>
                  <select
                    id="applicant-filter"
                    value={filters.applicant || ''}
                    onChange={(e) => handleFilterChange({ applicant: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All applicants</option>
                    {uniqueApplicants.map(applicant => (
                      <option key={applicant.id} value={applicant.id}>{applicant.label}</option>
                    ))}
                  </select>
                </div>
                
                {/* Date After filter */}
                <div className="filter-group">
                  <label htmlFor="date-after-filter">Date After</label>
                  <input
                    id="date-after-filter"
                    type="date"
                    value={filters.dateAfter || ''}
                    onChange={(e) => handleFilterChange({ dateAfter: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Date Before filter */}
                <div className="filter-group">
                  <label htmlFor="date-before-filter">Date Before</label>
                  <input
                    id="date-before-filter"
                    type="date"
                    value={filters.dateBefore || ''}
                    onChange={(e) => handleFilterChange({ dateBefore: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
                
                {/* Status filter */}
                <div className="filter-group">
                  <label htmlFor="status-filter">Status</label>
                  <select
                    id="status-filter"
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange({ status: e.target.value || null })}
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  >
                    <option value="">All statuses</option>
                    <option value="Y">Successful</option>
                    <option value="N">Unsuccessful</option>
                    <option value="P">Under Review</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Sort controls removed - now using column headers for sorting */}
        </div>
      </div>
      
      <div className="enhanced-eoi-responses-table-container enhanced-table-force-scroll">
        <EnhancedEOIResponsesTable
          columns={columns}
          data={paginatedResponses}
          keyField="reoi_id"
          width="1200px"
          maxHeight="calc(100vh - 20rem)"
          loading={isLoading}
          emptyMessage="No EOI responses found"
          horizontalScroll={true}
          alternateRowColors={true}
          pagination={paginationConfig}
          defaultSort={sortConfig}
          onSort={handleSort}
          permissions={{
            isAdmin,
            canManageSystemSettings
          }}
        />
      </div>

      {/* Add EOI Response Modal */}
      {isFormModalOpen && !selectedResponse && (
        <AddEOIResponseModal
          isOpen={isFormModalOpen}
          onClose={() => {
            setIsFormModalOpen(false);
          }}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ['eoi-responses'] });
          }}
        />
      )}

      {/* Edit EOI Response Modal */}
      {isFormModalOpen && selectedResponse && (
        <EditEOIResponseModal
          isOpen={isFormModalOpen}
          onClose={() => {
            setIsFormModalOpen(false);
            setSelectedResponse(undefined);
          }}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ['eoi-responses'] });
          }}
          eoiResponse={selectedResponse}
        />
      )}
    </div>
  );
}
